(() => {
  const SELECTOR = '[data-pm-delivery-picker]';
  const initialized = new WeakSet();

  const moneyLabel = 'PHP 99.00';
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
  const dateRules = window.PetMealsDeliveryDateRules;
  const deliveryFees = window.PetMealsDeliveryFees;
  const deliveryConfig = window.PetMealsDeliveryConfig || {};
  const serverTimeMs = Number(deliveryConfig.serverTimeMs);
  const monotonicStart = window.performance?.now?.() || 0;

  if (!dateRules || !deliveryFees) return;

  function getCurrentTimeMs() {
    const debugTimeMs = Number(window.__PM_DEBUG_TIME_OVERRIDE_MS__);
    if (Number.isFinite(debugTimeMs) && debugTimeMs > 0) return debugTimeMs;

    if (!Number.isFinite(serverTimeMs)) return Date.now();
    return serverTimeMs + Math.max(0, (window.performance?.now?.() || 0) - monotonicStart);
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function toDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function dateFromKey(dateKey) {
    const parts = dateRules.parseDateKey(dateKey);
    return parts ? new Date(parts.year, parts.month - 1, parts.day) : null;
  }

  function sameMonth(dateA, dateB) {
    return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth();
  }

  function isMonthBefore(dateA, dateB) {
    return (
      dateA.getFullYear() < dateB.getFullYear() ||
      (dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() < dateB.getMonth())
    );
  }

  function getDateUnavailableMessage(deliveryPeriod = '') {
    const earliestDate = dateRules.formatDateKey(
      dateRules.getEarliestSelectableDateKey(getCurrentTimeMs(), deliveryPeriod)
    );
    const isDayBefore = dateRules.getDeliveryPeriodTiming(deliveryPeriod) === 'day_before';

    return [
      isDayBefore ? deliveryConfig.dayBeforeUnavailable : deliveryConfig.dateUnavailable,
      `${
        isDayBefore
          ? deliveryConfig.earliestDayBeforeLabel
          : deliveryConfig.earliestDeliveryLabel
      } ${earliestDate}.`,
      isDayBefore ? '' : deliveryConfig.cutoffExplanation,
    ]
      .filter(Boolean)
      .join(' ');
  }

  function getCurrentVariantId(form) {
    const variantInput = form.querySelector('input[name="id"]');
    return variantInput ? variantInput.value : '';
  }

  function getQuantityInput(form, picker) {
    if (!form) return null;

    return (
      form.querySelector('input[name="quantity"]') ||
      document.querySelector(`input[name="quantity"][form="${form.id}"]`) ||
      picker?.closest('product-info')?.querySelector('input[name="quantity"]') ||
      null
    );
  }

  function getQuantity(form, picker) {
    const quantityInput = getQuantityInput(form, picker);
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function parseMoneyToCents(value) {
    if (!value) return 0;

    const normalizedValue = String(value).replace(/,/g, '');
    const amount = Number(normalizedValue.replace(/[^\d.]/g, ''));

    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function formatMoney(cents) {
    const amount = Math.max(0, cents) / 100;
    return `PHP ${amount.toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  function getProductPriceCents(picker) {
    const price = parseInt(picker.dataset.productPrice, 10);
    return Number.isFinite(price) && price >= 0 ? price : 0;
  }

  function getDeliveryFeeCents(picker) {
    return getShippingFee(picker, getSelectedArea(picker.querySelector('[data-pm-delivery-area]'))).cents;
  }

  function getShippingFee(picker, area) {
    return deliveryFees.getFeeForArea(area, picker.dataset.shippingFees);
  }

  function getSelectedArea(areaSelect) {
    if (!areaSelect || !areaSelect.value) return null;

    const selectedOption = areaSelect.options[areaSelect.selectedIndex];
    return {
      slug: areaSelect.value,
      name: selectedOption.dataset.areaName || selectedOption.textContent.trim(),
    };
  }

  async function fetchCurrentCart() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error('Unable to check the current cart. Please try again.');
    return response.json();
  }

  function getExistingShippingDates(cart) {
    return new Set(
      (cart?.items || [])
        .filter((item) => item.properties?._shipfee === 'true')
        .flatMap((item) => {
          const deliveryDate = item.properties?.['Delivery Date'];
          const deliveryPeriod = item.properties?.['Delivery Period'];
          if (!deliveryDate) return [];

          return deliveryPeriod
            ? [deliveryDate, `${deliveryDate}::${deliveryPeriod}`]
            : [deliveryDate];
        })
        .filter(Boolean)
    );
  }

  function getCartAreaConflict(cart, area) {
    const targetArea = area?.slug || '';
    if (!targetArea) return false;

    return (cart?.items || []).some((item) => {
      if (item.properties?._shipfee === 'true') return false;
      if (!item.properties?.['Selected Consumption Dates']) return false;

      const itemArea = item.properties?.['Delivery Location'] || '';
      return itemArea && itemArea !== targetArea;
    });
  }

  function buildPayload({
    dates,
    area,
    form,
    picker,
    furbabyName,
    deliveryPeriod,
    existingShippingDates = new Set(),
  }) {
    const productVariantId = getCurrentVariantId(form);
    const productTitle = picker.dataset.productTitle || document.title;
    const shippingFee = getShippingFee(picker, area);
    const feeVariantId = shippingFee.variantId;
    const feeLabel = shippingFee.label;
    const quantity = getQuantity(form, picker);
    const items = [];

    dates
      .slice()
      .sort((a, b) => a - b)
      .forEach((date) => {
        const deliveryDate = formatDate(date);

        items.push({
          id: productVariantId,
          quantity,
          properties: {
            'Furbaby Name': furbabyName,
            'Selected Consumption Dates': deliveryDate,
            'Delivery Period': deliveryPeriod,
            'Delivery Location': area.slug,
            'Delivery Location Name': area.name,
            'Shipping Fee': feeLabel,
          },
        });

        const shippingDateKey = `${deliveryDate}::${deliveryPeriod}`;

        if (
          !existingShippingDates.has(shippingDateKey) &&
          !existingShippingDates.has(deliveryDate)
        ) {
          items.push({
            id: feeVariantId,
            quantity: 1,
            properties: {
              _shipfee: 'true',
              'Furbaby Name': furbabyName,
              'Delivery Date': deliveryDate,
              'Delivery Period': deliveryPeriod,
              'Delivery Location': area.slug,
              'Delivery Location Name': area.name,
              'Delivery Fee': feeLabel,
              'Linked Product': productTitle,
            },
          });
          existingShippingDates.add(deliveryDate);
          existingShippingDates.add(shippingDateKey);
        }
      });

    return items;
  }

  function updateVariantPriceFromEvent(picker, variant) {
    if (!variant || !Number.isFinite(variant.price)) return;
    picker.dataset.productPrice = String(variant.price);
  }

  function setError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  function setButtonState(button, enabled, text) {
    if (!button) return;
    button.disabled = !enabled;
    button.setAttribute('aria-disabled', String(!enabled));

    const span = button.querySelector('span');
    if (span && text) span.textContent = text;
  }

  function createCalendar({
    mount,
    selectedDates,
    getDeliveryPeriod,
    onChange,
    onDatesInvalidated,
    onAvailabilityChanged,
  }) {
    const initialDeliveryPeriod = getDeliveryPeriod();
    const initialEarliestDate = dateFromKey(
      dateRules.getEarliestSelectableDateKey(getCurrentTimeMs(), initialDeliveryPeriod)
    );
    let visibleMonth = new Date(initialEarliestDate.getFullYear(), initialEarliestDate.getMonth(), 1);
    let availabilitySignature = '';
    let availabilityTimer = null;
    const selected = new Map();

    function syncSelectedDates() {
      selectedDates.splice(0, selectedDates.length, ...Array.from(selected.values()).sort((a, b) => a - b));
      onChange();
    }

    function removeUnavailableSelections(timestampMs) {
      let removedCount = 0;
      const deliveryPeriod = getDeliveryPeriod();

      selected.forEach((date, key) => {
        if (dateRules.isDeliveryDateAvailable(key, timestampMs, deliveryPeriod)) return;
        selected.delete(key);
        removedCount += 1;
      });

      return removedCount;
    }

    function render() {
      const timestampMs = getCurrentTimeMs();
      const deliveryPeriod = getDeliveryPeriod();
      const todayKey = dateRules.getManilaDateKey(timestampMs);
      const earliestDate = dateFromKey(
        dateRules.getEarliestSelectableDateKey(timestampMs, deliveryPeriod)
      );

      if (isMonthBefore(visibleMonth, earliestDate)) {
        visibleMonth = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
      }

      mount.innerHTML = '';
      mount.className = 'pm-delivery-calendar';

      const nav = document.createElement('div');
      nav.className = 'pm-delivery-calendar__nav';

      const prevButton = document.createElement('button');
      prevButton.className = 'pm-delivery-calendar__nav-button';
      prevButton.type = 'button';
      prevButton.textContent = '<';
      prevButton.setAttribute('aria-label', 'Previous month');
      prevButton.disabled = sameMonth(visibleMonth, earliestDate);

      const title = document.createElement('div');
      title.className = 'pm-delivery-calendar__title';
      title.textContent = monthFormatter.format(visibleMonth);

      const nextButton = document.createElement('button');
      nextButton.className = 'pm-delivery-calendar__nav-button';
      nextButton.type = 'button';
      nextButton.textContent = '>';
      nextButton.setAttribute('aria-label', 'Next month');

      nav.append(prevButton, title, nextButton);

      const weekdayRow = document.createElement('div');
      weekdayRow.className = 'pm-delivery-calendar__weekdays';
      weekdays.forEach((weekday) => {
        const cell = document.createElement('div');
        cell.className = 'pm-delivery-calendar__weekday';
        cell.textContent = weekday;
        weekdayRow.appendChild(cell);
      });

      const grid = document.createElement('div');
      grid.className = 'pm-delivery-calendar__grid';

      const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
      const daysInMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0).getDate();

      for (let index = 0; index < firstDay.getDay(); index += 1) {
        const blank = document.createElement('span');
        blank.className = 'pm-delivery-calendar__blank';
        grid.appendChild(blank);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day);
        const key = toDateKey(date);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const disabled = !dateRules.isDeliveryDateAvailable(
          key,
          timestampMs,
          deliveryPeriod
        );
        const button = document.createElement('button');

        button.className = 'pm-delivery-calendar__day';
        button.type = 'button';
        button.textContent = String(day);
        button.disabled = disabled;
        button.setAttribute('aria-label', `${formatDate(date)}${disabled ? ', unavailable' : ''}`);
        button.setAttribute('aria-pressed', selected.has(key) ? 'true' : 'false');

        if (key === todayKey) {
          button.classList.add('pm-delivery-calendar__day--today');
        }

        if (selected.has(key)) {
          button.classList.add('pm-delivery-calendar__day--selected');
        }

        if (disabled) {
          button.classList.add('pm-delivery-calendar__day--disabled');
          button.title = isWeekend
            ? 'No weekend deliveries'
            : getDateUnavailableMessage(deliveryPeriod);
        } else {
          button.addEventListener('click', () => {
            if (selected.has(key)) {
              selected.delete(key);
            } else {
              selected.set(key, date);
            }
            syncSelectedDates();
            render();
          });
        }

        grid.appendChild(button);
      }

      prevButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
        render();
      });

      nextButton.addEventListener('click', () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
        render();
      });

      mount.append(nav, weekdayRow, grid);
    }

    function refreshAvailability({ notify = true } = {}) {
      const timestampMs = getCurrentTimeMs();
      const removedCount = removeUnavailableSelections(timestampMs);

      if (removedCount > 0) syncSelectedDates();
      render();
      if (removedCount > 0 && notify) onDatesInvalidated?.();

      return removedCount;
    }

    function getAvailabilitySignature() {
      const timestampMs = getCurrentTimeMs();
      const deliveryPeriod = getDeliveryPeriod();
      return [
        dateRules.getManilaDateKey(timestampMs),
        dateRules.getMinimumDeliveryDateKey(timestampMs, deliveryPeriod),
        dateRules.getDeliveryPeriodTiming(deliveryPeriod),
      ].join('|');
    }

    availabilitySignature = getAvailabilitySignature();
    render();
    availabilityTimer = window.setInterval(() => {
      if (!mount.isConnected) {
        window.clearInterval(availabilityTimer);
        return;
      }

      const nextSignature = getAvailabilitySignature();
      if (nextSignature === availabilitySignature) return;

      availabilitySignature = nextSignature;
      refreshAvailability();
      onAvailabilityChanged?.();
    }, 30000);

    return {
      clear() {
        selected.clear();
        syncSelectedDates();
        render();
      },
      refreshAvailability,
    };
  }

  function getFocusableElements(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  function createPlaceholder(node, label) {
    if (!node?.parentNode) return null;

    const placeholder = document.createComment(`pm-mobile-purchase-${label}`);
    node.parentNode.insertBefore(placeholder, node);
    return placeholder;
  }

  function restoreNode(node, placeholder) {
    if (!node || !placeholder?.parentNode) return;
    placeholder.parentNode.insertBefore(node, placeholder);
  }

  function setMobileControlVisible(element, visible) {
    if (!element) return;
    element.classList.toggle('pm-mobile-purchase__control--hidden', !visible);
  }

  function hasOptionSelection(group) {
    if (!group) return false;

    const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
    if (radios.length > 0) return radios.some((radio) => radio.checked && !radio.disabled);

    const select = group.querySelector('select');
    return select ? Boolean(select.value) : true;
  }

  function initPicker(picker) {
    if (initialized.has(picker)) return;

    const sectionId = picker.dataset.sectionId;
    const productInfo = picker.closest('product-info');
    const form = document.getElementById(`product-form-${sectionId}`) || productInfo?.querySelector('form[action*="/cart/add"]');
    const submitButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const areaSelect = picker.querySelector('[data-pm-delivery-area]');
    let furbabyNameInput =
      productInfo?.querySelector('[data-pm-furbaby-name]') ||
      picker.querySelector('[data-pm-furbaby-name]');
    const dateInput = picker.querySelector('[data-pm-delivery-dates]');
    const calendarMount = document.createElement('div');
    const summaryEl = picker.querySelector('[data-pm-delivery-summary]');
    const mealPriceEl = picker.querySelector('[data-pm-summary-meal-price]');
    const quantityEl = picker.querySelector('[data-pm-summary-quantity]');
    const daysEl = picker.querySelector('[data-pm-summary-days]');
    const feeEl = picker.querySelector('[data-pm-summary-fee]');
    const totalEl = picker.querySelector('[data-pm-summary-total]');
    const summaryNoteEl = picker.querySelector('[data-pm-summary-note]');
    const errorEl = picker.querySelector('[data-pm-delivery-error]');
    const statusEl = picker.querySelector('[data-pm-delivery-status]');
    const originalButtonText = submitButton?.querySelector('span')?.textContent?.trim() || 'Add to cart';
    let selectedDates = [];
    let submitting = false;
    let furbabyNameValue = furbabyNameInput?.value || '';
    let boundFurbabyNameInput = null;
    let mobileFlow = null;
    let calendar = null;

    if (!form || !submitButton || !areaSelect || !furbabyNameInput || !dateInput) return;

    initialized.add(picker);

    function getVariantSelects() {
      return productInfo?.querySelector('variant-selects') || null;
    }

    function getVariantGroups() {
      const variantSelects = getVariantSelects();
      return variantSelects ? Array.from(variantSelects.querySelectorAll('.product-form__input')) : [];
    }

    function getDeliveryPeriodGroup() {
      const groups = getVariantGroups();
      return (
        groups.find((group) => {
          const label = group.querySelector('legend, label')?.textContent || '';
          const controlName =
            group.querySelector('select, input[type="radio"]')?.getAttribute('name') || '';
          return /delivery period/i.test(`${label} ${controlName}`);
        }) ||
        groups[1] ||
        null
      );
    }

    function getDeliveryPeriodControls() {
      const group = getDeliveryPeriodGroup();
      if (!group) return [];

      const select = group.querySelector('select');
      return select
        ? Array.from(select.options)
        : Array.from(group.querySelectorAll('input[type="radio"]'));
    }

    function getSelectedDeliveryPeriod() {
      const group = getDeliveryPeriodGroup();
      if (!group) return '';

      const select = group.querySelector('select');
      if (select) return select.value;

      return group.querySelector('input[type="radio"]:checked')?.value || '';
    }

    function setPeriodControlCutoffDisabled(control, disabled) {
      if (disabled) {
        if (!control.disabled) {
          control.dataset.pmPeriodCutoffDisabled = 'true';
          control.disabled = true;
        }
        return;
      }

      if (control.dataset.pmPeriodCutoffDisabled === 'true') {
        control.disabled = false;
        delete control.dataset.pmPeriodCutoffDisabled;
      }
    }

    function getDayBeforeCutoffMessage() {
      const earliestDate = dateRules.formatDateKey(
        dateRules.getEarliestSelectableDateKey(getCurrentTimeMs(), 'DAY BEFORE')
      );

      return [
        deliveryConfig.dayBeforeUnavailable,
        `${deliveryConfig.earliestDayBeforeLabel} ${earliestDate}.`,
      ]
        .filter(Boolean)
        .join(' ');
    }

    function dayBeforeIsAvailableForSelection() {
      const timestampMs = getCurrentTimeMs();
      const dateKeys =
        selectedDates.length > 0
          ? selectedDates.map(toDateKey)
          : [dateRules.getEarliestSelectableDateKey(timestampMs)];

      return dateKeys.every((dateKey) =>
        dateRules.isDeliveryDateAvailable(dateKey, timestampMs, 'DAY BEFORE')
      );
    }

    function selectAvailableSameDayPeriod() {
      const group = getDeliveryPeriodGroup();
      if (!group) return false;

      const select = group.querySelector('select');
      if (select) {
        const sameDayOption = Array.from(select.options).find(
          (option) =>
            dateRules.getDeliveryPeriodTiming(option.value) === 'same_day' &&
            !option.disabled
        );
        if (!sameDayOption) return false;

        select.value = sameDayOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const sameDayRadio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
        (radio) =>
          dateRules.getDeliveryPeriodTiming(radio.value) === 'same_day' &&
          !radio.disabled &&
          !radio.classList.contains('disabled')
      );
      if (!sameDayRadio) return false;

      sameDayRadio.checked = true;
      sameDayRadio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    function syncDeliveryPeriodAvailability() {
      const group = getDeliveryPeriodGroup();
      if (!group) return false;

      const dayBeforeControls = getDeliveryPeriodControls().filter(
        (control) => dateRules.getDeliveryPeriodTiming(control.value) === 'day_before'
      );
      const dayBeforeAvailable = dayBeforeIsAvailableForSelection();

      dayBeforeControls.forEach((control) =>
        setPeriodControlCutoffDisabled(control, !dayBeforeAvailable)
      );

      let note = group.querySelector('[data-pm-day-before-cutoff-note]');
      if (!dayBeforeAvailable && dayBeforeControls.length > 0) {
        if (!note) {
          note = document.createElement('p');
          note.className = 'pm-delivery-picker__note';
          note.dataset.pmDayBeforeCutoffNote = '';
          group.appendChild(note);
        }
        note.textContent = getDayBeforeCutoffMessage();
      } else {
        note?.remove();
      }

      const selectedTiming = dateRules.getDeliveryPeriodTiming(
        getSelectedDeliveryPeriod()
      );
      return (
        selectedTiming === 'day_before' &&
        !dayBeforeAvailable &&
        selectAvailableSameDayPeriod()
      );
    }

    function getFurbabyNameInput() {
      const currentInput = productInfo?.querySelector('[data-pm-furbaby-name]') || picker.querySelector('[data-pm-furbaby-name]');
      if (!currentInput) return null;

      if (currentInput !== furbabyNameInput) {
        if (furbabyNameValue && !currentInput.value) currentInput.value = furbabyNameValue;
        furbabyNameInput = currentInput;
      }

      bindFurbabyNameInput();
      return furbabyNameInput;
    }

    function getFurbabyName() {
      return (getFurbabyNameInput()?.value || furbabyNameValue).trim();
    }

    function getValidationMessage() {
      const deliveryPeriod = getSelectedDeliveryPeriod();
      const timestampMs = getCurrentTimeMs();
      const selectedArea = getSelectedArea(areaSelect);

      if (!selectedArea) return 'Choose your delivery area.';
      if (!deliveryFees.hasConfiguredVariant(getShippingFee(picker, selectedArea))) return 'Delivery fee variant ID is not configured yet.';
      if (!getFurbabyName()) return "Tell us your furbaby's name.";
      if (!deliveryPeriod) return 'Choose a delivery period.';
      if (selectedDates.length === 0) return 'Choose at least one weekday delivery date.';
      if (
        selectedDates.some((date) =>
          !dateRules.isDeliveryDateAvailable(toDateKey(date), timestampMs)
        )
      ) {
        return getDateUnavailableMessage();
      }
      if (
        selectedDates.some(
          (date) =>
            !dateRules.isDeliveryDateAvailable(
              toDateKey(date),
              timestampMs,
              deliveryPeriod
            )
        )
      ) {
        return getDateUnavailableMessage(deliveryPeriod);
      }
      if (!getCurrentVariantId(form)) return 'Choose an available product option.';
      return '';
    }

    function updateSummary() {
      const area = getSelectedArea(areaSelect);
      const dateCount = selectedDates.length;
      const shippingFee = getShippingFee(picker, area);
      const feeLabel = shippingFee.label;
      const furbabyName = getFurbabyName();
      const quantity = getQuantity(form, picker);
      const mealPriceCents = getProductPriceCents(picker);
      const deliveryFeeCents = shippingFee.cents;
      const mealSubtotalCents = mealPriceCents * quantity * dateCount;
      const deliveryTotalCents = deliveryFeeCents * dateCount;
      const grandTotalCents = mealSubtotalCents + deliveryTotalCents;

      if (mealPriceEl) mealPriceEl.textContent = formatMoney(mealPriceCents);
      picker.dataset.shippingFeeVariantId = shippingFee.variantId;
      picker.dataset.shippingFeeLabel = shippingFee.label;
      if (quantityEl) quantityEl.textContent = String(quantity);
      if (daysEl) daysEl.textContent = String(dateCount);
      if (feeEl) {
        feeEl.textContent = dateCount > 0
          ? `${formatMoney(deliveryTotalCents)} (${feeLabel} x ${dateCount})`
          : feeLabel;
      }
      if (totalEl) totalEl.textContent = formatMoney(grandTotalCents);

      if (area && dateCount > 0) {
        const nameText = furbabyName ? ` for ${furbabyName}` : '';
        if (summaryNoteEl) {
          summaryNoteEl.textContent = `${dateCount} delivery ${dateCount === 1 ? 'date' : 'dates'}${nameText} in ${area.name}.`;
        }
      } else {
        if (summaryNoteEl) {
          summaryNoteEl.textContent = 'Add your furbaby name, area, and delivery dates to continue.';
        }
      }

      summaryEl?.classList.toggle('pm-delivery-picker__summary--ready', Boolean(area && dateCount > 0));
    }

    function updateState() {
      const deliveryPeriodChanged = syncDeliveryPeriodAvailability();
      if (deliveryPeriodChanged) calendar?.refreshAvailability({ notify: false });

      const message = getValidationMessage();
      updateSummary();
      setError(errorEl, message);
      setButtonState(submitButton, !message && !submitting, submitting ? 'Adding...' : originalButtonText);
      mobileFlow?.update();
    }

    function handleFurbabyNameInput(event) {
      furbabyNameValue = event.target.value;
      updateState();
    }

    function bindFurbabyNameInput() {
      if (!furbabyNameInput || furbabyNameInput === boundFurbabyNameInput) return;
      furbabyNameInput.addEventListener('input', handleFurbabyNameInput);
      boundFurbabyNameInput = furbabyNameInput;
    }

    function createMobilePurchaseFlow() {
      const root = productInfo?.querySelector(`[data-pm-mobile-purchase][data-section-id="${sectionId}"]`);
      const drawer = root?.querySelector('.pm-mobile-purchase__drawer');
      const bar = productInfo?.querySelector('[data-pm-mobile-purchase-bar]');
      const openButton = bar?.querySelector('[data-pm-mobile-purchase-open]');
      const closeButtons = root ? Array.from(root.querySelectorAll('[data-pm-mobile-purchase-close]')) : [];
      const stepSections = root ? Array.from(root.querySelectorAll('[data-pm-mobile-step]')) : [];
      const stepTriggers = root ? Array.from(root.querySelectorAll('[data-pm-mobile-step-trigger]')) : [];
      const stepCount = root?.querySelector('[data-pm-mobile-step-count]');
      const stepError = root?.querySelector('[data-pm-mobile-step-error]');
      const variantLoadingEl = root?.querySelector('[data-pm-mobile-variant-loading]');
      const variantLoadingText = root?.querySelector('[data-pm-mobile-variant-loading-text]');
      const backButton = root?.querySelector('[data-pm-mobile-step-back]');
      const nextButton = root?.querySelector('[data-pm-mobile-step-next]');
      const footerFormSlot = root?.querySelector('[data-pm-mobile-slot="footer-form"]');
      const barPrice = bar?.querySelector('[data-pm-mobile-bar-price]');
      const mediaQuery = window.matchMedia('(max-width: 749px)');
      const productForm = submitButton.closest('product-form');
      const quantityBlock = productInfo?.querySelector(`#Quantity-Form-${sectionId}`);
      const areaField = areaSelect.closest('.pm-delivery-picker__field');
      const dateField = dateInput.closest('.pm-delivery-picker__field');
      const pickerHeader = picker.querySelector('.pm-delivery-picker__header');
      const variantPlaceholder = createPlaceholder(getVariantSelects(), 'variant');
      const pickerPlaceholder = createPlaceholder(picker, 'picker');
      const quantityPlaceholder = createPlaceholder(quantityBlock, 'quantity');
      const formPlaceholder = createPlaceholder(productForm, 'form');
      let enabled = false;
      let open = false;
      let activeStep = 0;
      let lastFocusedElement = null;
      let visibleStepError = '';
      let isVariantLoading = false;
      let variantLoadingTimeout = null;

      if (
        !root ||
        !drawer ||
        !bar ||
        !openButton ||
        stepSections.length === 0 ||
        stepTriggers.length === 0 ||
        !backButton ||
        !nextButton ||
        !productForm ||
        !quantityBlock ||
        !variantPlaceholder ||
        !pickerPlaceholder ||
        !quantityPlaceholder ||
        !formPlaceholder
      ) {
        return null;
      }

      function getStepSlot(step, name) {
        return root.querySelector(`[data-pm-mobile-step="${step}"] [data-pm-mobile-slot="${name}"]`);
      }

      function appendToSlot(slot, node) {
        if (!slot || !node || node.parentNode === slot) return;
        slot.appendChild(node);
      }

      function setVariantLoading(loading, message = 'Updating choices...') {
        isVariantLoading = loading;
        window.clearTimeout(variantLoadingTimeout);

        if (loading && variantLoadingText) {
          variantLoadingText.textContent = message;
          variantLoadingTimeout = window.setTimeout(() => setVariantLoading(false), 8000);
        }

        render();
      }

      function setStepError(message) {
        visibleStepError = message;
        if (!stepError) return;
        stepError.textContent = message;
        stepError.hidden = !message;
      }

      function validateStep(step) {
        const groups = getVariantGroups();

        if (step === 0) {
          return getFurbabyName() ? '' : "Tell us your furbaby's name.";
        }

        if (step === 1) {
          return hasOptionSelection(groups[0]) ? '' : 'Choose a pack size.';
        }

        if (step === 2) {
          const deliveryPeriodGroup = getDeliveryPeriodGroup();
          if (!hasOptionSelection(deliveryPeriodGroup)) return 'Choose a delivery period.';

          return dateRules.getDeliveryPeriodTiming(getSelectedDeliveryPeriod()) ===
            'day_before' && !dayBeforeIsAvailableForSelection()
            ? getDayBeforeCutoffMessage()
            : '';
        }

        if (step === 3) {
          return getSelectedArea(areaSelect) ? '' : 'Choose your delivery area.';
        }

        return getValidationMessage();
      }

      function moveActiveNodes() {
        const variantSelects = getVariantSelects();

        if (activeStep <= 2 && variantSelects) {
          appendToSlot(getStepSlot(activeStep, 'variant'), variantSelects);
        }

        if (activeStep === 3) {
          appendToSlot(getStepSlot(3, 'area'), picker);
        }

        if (activeStep === 4) {
          appendToSlot(getStepSlot(4, 'dates'), picker);
        }

        appendToSlot(getStepSlot(3, 'quantity'), quantityBlock);
        const formSlot =
          activeStep === stepSections.length - 1
            ? footerFormSlot || getStepSlot(4, 'form')
            : getStepSlot(4, 'form');
        appendToSlot(formSlot, productForm);
      }

      function syncVariantControls() {
        const variantSelects = getVariantSelects();
        const groups = getVariantGroups();
        const furbabyField = getFurbabyNameInput()?.closest('.pm-furbaby-field');

        if (!enabled) {
          [furbabyField, ...groups].forEach((element) => setMobileControlVisible(element, true));
          return;
        }

        setMobileControlVisible(furbabyField, activeStep === 0);
        groups.forEach((group, index) => {
          setMobileControlVisible(group, activeStep === index + 1);
        });

        variantSelects?.classList.toggle('pm-mobile-purchase__variant-host', activeStep <= 2);
      }

      function syncPickerControls() {
        if (!enabled) {
          [pickerHeader, areaField, dateField, summaryEl, errorEl, statusEl].forEach((element) =>
            setMobileControlVisible(element, true)
          );
          picker.classList.remove('pm-mobile-purchase__picker-host');
          return;
        }

        picker.classList.add('pm-mobile-purchase__picker-host');
        setMobileControlVisible(pickerHeader, false);
        setMobileControlVisible(areaField, activeStep === 3);
        setMobileControlVisible(dateField, activeStep === 4);
        setMobileControlVisible(summaryEl, activeStep === 4);
        setMobileControlVisible(errorEl, activeStep === 4);
        setMobileControlVisible(statusEl, activeStep === 4);
      }

      function render() {
        if (!enabled) return;

        moveActiveNodes();
        syncVariantControls();
        syncPickerControls();

        stepSections.forEach((section) => {
          const isActive = Number(section.dataset.pmMobileStep) === activeStep;
          section.hidden = !isActive;
          section.classList.toggle('is-active', isActive);
        });

        stepTriggers.forEach((trigger) => {
          const step = Number(trigger.dataset.pmMobileStepTrigger);
          const complete = step < activeStep && !validateStep(step);
          trigger.classList.toggle('is-active', step === activeStep);
          trigger.classList.toggle('is-complete', complete);
          trigger.setAttribute('aria-current', step === activeStep ? 'step' : 'false');
        });

        if (stepCount) stepCount.textContent = `Step ${activeStep + 1} of ${stepSections.length}`;
        const showVariantLoading = isVariantLoading && activeStep > 0 && activeStep <= 2;
        root.classList.toggle('is-loading-variant', showVariantLoading);
        drawer.setAttribute('aria-busy', String(showVariantLoading));
        if (variantLoadingEl) variantLoadingEl.hidden = !showVariantLoading;
        backButton.disabled = activeStep === 0;
        nextButton.hidden = activeStep === stepSections.length - 1;
        nextButton.disabled = isVariantLoading && activeStep === 2;
        if (footerFormSlot) footerFormSlot.hidden = activeStep !== stepSections.length - 1;
        if (barPrice) barPrice.textContent = formatMoney(getProductPriceCents(picker));

        if (visibleStepError && !validateStep(activeStep)) setStepError('');
      }

      function goToStep(nextStep, shouldFocus = true) {
        const targetStep = Math.max(0, Math.min(stepSections.length - 1, nextStep));

        if (isVariantLoading && activeStep <= 2 && targetStep > 2) {
          render();
          return false;
        }

        if (targetStep > activeStep) {
          for (let step = activeStep; step < targetStep; step += 1) {
            const message = validateStep(step);
            if (message) {
              activeStep = step;
              setStepError(message);
              render();
              if (shouldFocus) focusActiveStep();
              return false;
            }
          }
        }

        activeStep = targetStep;
        setStepError('');
        render();
        if (shouldFocus) focusActiveStep();
        return true;
      }

      function focusActiveStep() {
        const activeSection = stepSections[activeStep];
        const focusable = getFocusableElements(activeSection);
        (focusable[0] || drawer).focus({ preventScroll: true });
      }

      function handleKeydown(event) {
        if (!open) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = getFocusableElements(drawer);
        if (focusable.length === 0) {
          event.preventDefault();
          drawer.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      function openDrawer() {
        if (!enabled) return;
        lastFocusedElement = document.activeElement;
        open = true;
        root.hidden = false;
        root.setAttribute('aria-hidden', 'false');
        openButton.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('pm-mobile-order-open');
        document.addEventListener('keydown', handleKeydown);
        render();
        window.requestAnimationFrame(() => focusActiveStep());
      }

      function closeDrawer(shouldRestoreFocus = true) {
        open = false;
        root.setAttribute('aria-hidden', 'true');
        openButton.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('pm-mobile-order-open');
        document.removeEventListener('keydown', handleKeydown);
        setStepError('');
        if (shouldRestoreFocus && lastFocusedElement?.focus) {
          lastFocusedElement.focus({ preventScroll: true });
        }
      }

      function enable() {
        if (enabled) return;
        enabled = true;
        root.hidden = false;
        bar.hidden = false;
        root.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.add('pm-mobile-order-ready');
        activeStep = Math.min(activeStep, stepSections.length - 1);
        render();
      }

      function disable() {
        if (!enabled) return;
        closeDrawer(false);
        enabled = false;
        restoreNode(getVariantSelects(), variantPlaceholder);
        restoreNode(picker, pickerPlaceholder);
        restoreNode(quantityBlock, quantityPlaceholder);
        restoreNode(productForm, formPlaceholder);
        syncVariantControls();
        syncPickerControls();
        root.hidden = true;
        bar.hidden = true;
        document.documentElement.classList.remove('pm-mobile-order-ready');
      }

      function syncLayout() {
        if (mediaQuery.matches) {
          enable();
        } else {
          disable();
        }
      }

      openButton.addEventListener('click', openDrawer);
      closeButtons.forEach((button) => button.addEventListener('click', () => closeDrawer()));
      backButton.addEventListener('click', () => goToStep(activeStep - 1));
      nextButton.addEventListener('click', () => goToStep(activeStep + 1));
      stepTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => goToStep(Number(trigger.dataset.pmMobileStepTrigger)));
      });

      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', syncLayout);
      } else {
        mediaQuery.addListener(syncLayout);
      }

      syncLayout();

      return {
        update: render,
        startVariantLoading(target) {
          const groups = getVariantGroups();
          const groupIndex = groups.findIndex((group) => group.contains(target));
          const message = groupIndex === 0 ? 'Updating delivery period...' : 'Updating choices...';
          setVariantLoading(true, message);
        },
        stopVariantLoading() {
          setVariantLoading(false);
        },
        refreshAfterVariantChange() {
          setVariantLoading(false);
          bindFurbabyNameInput();
          render();
        },
        showValidation(message) {
          if (!enabled) return;
          setStepError(message);
          openDrawer();
        },
      };
    }

    dateInput.insertAdjacentElement('afterend', calendarMount);
    calendar = createCalendar({
      mount: calendarMount,
      selectedDates,
      getDeliveryPeriod: getSelectedDeliveryPeriod,
      onChange: () => {
        dateInput.value = selectedDates.map(formatDate).join(', ');
        updateState();
      },
      onDatesInvalidated: () => {
        const message = getDateUnavailableMessage(getSelectedDeliveryPeriod());
        setError(errorEl, message);
        mobileFlow?.showValidation(message);
      },
      onAvailabilityChanged: updateState,
    });

    areaSelect.addEventListener('change', updateState);
    bindFurbabyNameInput();
    const quantityInput = getQuantityInput(form, picker);
    quantityInput?.addEventListener('change', updateState);
    quantityInput?.addEventListener('input', updateState);
    form.querySelector('input[name="id"]')?.addEventListener('change', updateState);
    mobileFlow = createMobilePurchaseFlow();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        if (!data?.event?.target || !productInfo.contains(data.event.target)) return;
        if (!data.target?.matches?.('input[type="radio"]') && data.target?.tagName !== 'OPTION') return;
        mobileFlow?.startVariantLoading(data.target);
      });

      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== sectionId) return;
        updateVariantPriceFromEvent(picker, data.variant);
        mobileFlow?.refreshAfterVariantChange();
        calendar.refreshAvailability();
        updateState();
      });
    }

    form.addEventListener('submit', async (event) => {
      if (!picker.isConnected) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const invalidatedDateCount = calendar.refreshAvailability({ notify: false });
      if (invalidatedDateCount > 0) {
        const cutoffMessage = getDateUnavailableMessage(getSelectedDeliveryPeriod());
        updateState();
        setError(errorEl, cutoffMessage);
        mobileFlow?.showValidation(cutoffMessage);
        return;
      }

      const message = getValidationMessage();
      if (message) {
        setError(errorEl, message);
        mobileFlow?.showValidation(message);
        updateState();
        return;
      }

      const area = getSelectedArea(areaSelect);
      const furbabyName = getFurbabyName();
      const deliveryPeriod = getSelectedDeliveryPeriod();

      submitting = true;
      setError(errorEl, '');
      if (statusEl) statusEl.textContent = 'Checking shared delivery fees...';
      updateState();

      try {
        const cart = await fetchCurrentCart();
        if (getCartAreaConflict(cart, area)) {
          throw new Error('Your cart already has a different delivery area. Open the cart to update it before adding more meals.');
        }

        const existingShippingDates = getExistingShippingDates(cart);
        const items = buildPayload({
          dates: selectedDates,
          area,
          form,
          picker,
          furbabyName,
          deliveryPeriod,
          existingShippingDates,
        });

        if (statusEl) statusEl.textContent = 'Adding delivery dates to cart...';

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'Unable to add delivery items.');
        }

        calendar.clear();
        if (statusEl) statusEl.textContent = 'Added to cart.';
        window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        setError(errorEl, error.message || 'Unable to add to cart. Please try again.');
        if (statusEl) statusEl.textContent = '';
      } finally {
        submitting = false;
        updateState();
      }
    }, true);

    updateState();
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((picker) => {
      initPicker(picker);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
